/**
 * mobile_controller.js — global mobile navigation / UX (Stimulus).
 *
 * Ports static/js/mobile.js to an element-scoped controller hosted on a
 * top-level wrapper present on every page (base.html's main content element).
 * The behaviour is whole-page mobile UX: the slide-in menu overlay, business
 * switching, touch feedback on .mobile-card, and the iOS no-zoom input tweak.
 *
 * Lifecycle (replaces the old attachOnce()/turbo:load once-guard):
 *   The original wrapped initializeMobileEventListeners() in an attachOnce()
 *   that registered it on turbo:load, so it re-bound to the freshly swapped DOM
 *   on every Turbo navigation without stacking the turbo:load listener.
 *   Stimulus does that structurally: connect() runs each time the controller's
 *   element is inserted (Turbo replaces <body> on navigation), so we simply run
 *   the init in connect(). The listeners it binds attach to specific elements
 *   Turbo replaces wholesale, so they cannot stack across navigations.
 *
 *   The pageshow handler is window-scoped page lifecycle (iOS bfcache restore),
 *   not DOM-element wiring, so it must be added once and removed on disconnect
 *   to avoid stacking across navigations. The controller instance owns add +
 *   remove, which is the attachOnce replacement for that listener.
 *
 * Stateless menu helpers stay module-level and are exposed on window for the
 * template dispatcher (data-action="call-fn" in global-actions.js).
 */

import { Controller } from "@hotwired/stimulus";

    // Mobile navigation functions
    function showMobileMenu() {
        const overlay = document.getElementById('mobileMenuOverlay');
        if (overlay) {
            overlay.classList.add('show');
            overlay.style.display = 'block';
            // Ensure main menu is shown when opening
            showMainMenu();
            // The native shells' More tab opens this menu while the full-screen
            // Chip sheet may be up; both sit at z-index 1050 and the widget comes
            // later in the DOM, so it would cover the menu. The widget listens
            // for this and closes (ai_chat_widget.html).
            document.dispatchEvent(new CustomEvent('chippie:mobile-menu-open'));
        }
    }

    function hideMobileMenu() {
        const overlay = document.getElementById('mobileMenuOverlay');
        if (overlay) {
            overlay.classList.remove('show');
            setTimeout(() => {
                overlay.style.display = 'none';
            }, 300);
        }
    }

    // Closed at once, with no fade: Turbo snapshots the page synchronously
    // after turbo:before-cache, and a menu cached open covers the page on Back.
    function closeMobileMenuNow() {
        const overlay = document.getElementById('mobileMenuOverlay');
        if (overlay) {
            overlay.classList.remove('show');
            overlay.style.display = 'none';
        }
    }

    function showBusinessList() {
        const mainMenu = document.getElementById('mobileMainMenu');
        const businessMenu = document.getElementById('mobileBusinessMenu');
        const menuTitle = document.getElementById('mobileMenuTitle');

        if (mainMenu && businessMenu && menuTitle) {
            mainMenu.style.display = 'none';
            businessMenu.style.display = 'block';
            menuTitle.textContent = 'Switch Business';
        }
    }

    function showMainMenu() {
        const mainMenu = document.getElementById('mobileMainMenu');
        const businessMenu = document.getElementById('mobileBusinessMenu');
        const menuTitle = document.getElementById('mobileMenuTitle');

        if (mainMenu && businessMenu && menuTitle) {
            mainMenu.style.display = 'block';
            businessMenu.style.display = 'none';
            menuTitle.textContent = 'Menu';
        }
    }

    // Initialize mobile menu event listeners.
    // Safe to re-run on every connect() — listeners are bound to specific DOM
    // elements which Turbo replaces wholesale on body swap; no listener stacking.
    function initializeMobileEventListeners() {
        // Close menu when clicking overlay
        const overlay = document.getElementById('mobileMenuOverlay');
        if (overlay) {
            overlay.addEventListener('click', function(e) {
                if (e.target === this) {
                    hideMobileMenu();
                }
            });
        }

        // Add event listeners for business switching (PWA-compatible alternative to onclick)
        const businessListButtons = document.querySelectorAll('[data-action="show-business-list"]');
        businessListButtons.forEach(button => {
            button.addEventListener('click', function(e) {
                e.preventDefault();
                showBusinessList();
            });
        });

        const mainMenuButtons = document.querySelectorAll('[data-action="show-main-menu"]');
        mainMenuButtons.forEach(button => {
            button.addEventListener('click', function(e) {
                e.preventDefault();
                showMainMenu();
            });
        });

        // Add touch feedback to mobile cards
        const mobileCards = document.querySelectorAll('.mobile-card');
        mobileCards.forEach(card => {
            card.addEventListener('touchstart', function() {
                this.style.transform = 'scale(0.98)';
            });

            card.addEventListener('touchend', function() {
                this.style.transform = 'scale(1)';
            });
        });

        // Prevent zoom on form inputs (iOS)
        const inputs = document.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            input.addEventListener('focus', function() {
                if (this.classList.contains('form-control-mobile')) {
                    // Already has 16px font size to prevent zoom
                    return;
                }
                // Add temporary style to prevent zoom
                this.style.fontSize = '16px';
            });

            input.addEventListener('blur', function() {
                // Remove temporary style
                this.style.fontSize = '';
            });
        });
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js).
    window.showMobileMenu = showMobileMenu;
    window.hideMobileMenu = hideMobileMenu;
    window.showBusinessList = showBusinessList;
    window.showMainMenu = showMainMenu;

export default class extends Controller {
        connect() {
            // Bind mobile nav to the freshly inserted DOM. Runs on every Turbo
            // navigation because Turbo replaces <body> (and this controller's
            // host) wholesale, so listeners rebind to fresh elements.
            initializeMobileEventListeners();

            // iOS bfcache restore does not re-fire DOMContentLoaded (and on some
            // versions does not fire visibilitychange). Send CHECK_VERSION
            // explicitly so the SW invalidates stale caches before the user
            // interacts with stale JS. Window-scoped; add once / remove on
            // disconnect so it cannot stack across navigations.
            this._onPageShow = function (event) {
                if (!event.persisted) return;
                if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                    navigator.serviceWorker.controller.postMessage({ type: 'CHECK_VERSION' });
                }
            };
            window.addEventListener('pageshow', this._onPageShow);

            this._onBeforeCache = closeMobileMenuNow;
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        disconnect() {
            if (this._onPageShow) {
                window.removeEventListener('pageshow', this._onPageShow);
                this._onPageShow = null;
            }
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
        }
}
